import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Sourcr] Uncaught error:', error, info);
  }

  override render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center' as const,
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              display: 'flex',
              flexDirection: 'column' as const,
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span style={{ fontSize: 32 }}>⚠️</span>
            <strong style={{ color: 'var(--text-primary)' }}>Something went wrong</strong>
            <span>{this.state.error?.message}</span>
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                backgroundColor: 'var(--bg-elevated)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Try again
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
